import { create } from 'zustand';
import { Company } from '../db/db';
import { LicenseInfo } from '../lib/license';

interface AppState {
  activeCompany: Company | null;
  setActiveCompany: (company: Company | null) => void;
  updateActiveCompanyFields: (fields: Partial<Company>) => void;
  switchingCompany: boolean;
  setSwitchingCompany: (val: boolean) => void;
  fromDate: string;
  setFromDate: (date: string) => void;
  toDate: string;
  setToDate: (date: string) => void;
  printRequested: boolean;
  setPrintRequested: (requested: boolean) => void;
  exportRequested: 'excel' | 'pdf' | null;
  setExportRequested: (requested: 'excel' | 'pdf' | null) => void;
  currentVoucherType: string;
  setCurrentVoucherType: (type: string) => void;
  licenseInfo: LicenseInfo | null;
  setLicenseInfo: (info: LicenseInfo | null) => void;
  showLicensePanel: boolean;
  setShowLicensePanel: (show: boolean) => void;
  systemDate: string;
  setSystemDate: (date: string) => void;
  globalMessage: string | null;
  setGlobalMessage: (msg: string | null) => void;
  // Voucher alter/duplicate mode — set synchronously before navigation
  // so VoucherEngineWrapper never receives a null voucherId
  currentAlterVoucherId: number | null;
  setCurrentAlterVoucherId: (id: number | null) => void;
  currentIsDuplicateMode: boolean;
  setCurrentIsDuplicateMode: (val: boolean) => void;
  currentQuotationId: number | null;
  setCurrentQuotationId: (id: number | null) => void;
  binboxData: any[] | null;
  setBinboxData: (data: any[] | null) => void;
  trialLimitReason: string | null;
  setTrialLimitReason: (reason: string | null) => void;
}

const getLocalSystemDate = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const useStore = create<AppState>((set) => ({
  activeCompany: null,
  setActiveCompany: (company) => {
    if (company) {
      localStorage.setItem('lastActiveCompanyId', company.id!.toString());
      
      const [year, month, day] = company.financialYearStart.split('-');
      const fyStart = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      const startStr = company.financialYearStart;
      
      const fyEnd = new Date(fyStart);
      fyEnd.setFullYear(fyEnd.getFullYear() + 1);
      fyEnd.setDate(fyEnd.getDate() - 1);
      
      const endStr = `${fyEnd.getFullYear()}-${String(fyEnd.getMonth() + 1).padStart(2, '0')}-${String(fyEnd.getDate()).padStart(2, '0')}`;

      set({ 
        activeCompany: company,
        fromDate: startStr,
        toDate: endStr,
        switchingCompany: false,
        // Reset voucher/binbox state on company change to prevent stale data bleed
        currentAlterVoucherId: null,
        currentIsDuplicateMode: false,
        currentQuotationId: null,
        binboxData: null,
      });
    } else {
      // Null → switching in progress, clear everything company-specific
      set({
        activeCompany: null,
        switchingCompany: true,
        currentAlterVoucherId: null,
        currentIsDuplicateMode: false,
        currentQuotationId: null,
        binboxData: null,
      });
    }
  },
  updateActiveCompanyFields: (fields) => {
    set((state) => state.activeCompany ? { activeCompany: { ...state.activeCompany, ...fields } } : {});
  },
  switchingCompany: false,
  setSwitchingCompany: (val) => set({ switchingCompany: val }),
  fromDate: '',
  setFromDate: (date) => set({ fromDate: date }),
  toDate: '',
  setToDate: (date) => set({ toDate: date }),
  printRequested: false,
  setPrintRequested: (requested) => set({ printRequested: requested }),
  exportRequested: null,
  setExportRequested: (requested) => set({ exportRequested: requested }),
  currentVoucherType: 'Sales',
  setCurrentVoucherType: (type) => set({ currentVoucherType: type }),
  licenseInfo: null,
  setLicenseInfo: (info) => set({ licenseInfo: info }),
  showLicensePanel: false,
  setShowLicensePanel: (show) => set({ showLicensePanel: show }),
  systemDate: getLocalSystemDate(),
  setSystemDate: (date) => set({ systemDate: date }),
  globalMessage: null,
  setGlobalMessage: (msg) => {
    set({ globalMessage: msg });
    if (msg) setTimeout(() => set({ globalMessage: null }), 3000);
  },
  currentAlterVoucherId: null,
  setCurrentAlterVoucherId: (id) => set({ currentAlterVoucherId: id }),
  currentIsDuplicateMode: false,
  setCurrentIsDuplicateMode: (val) => set({ currentIsDuplicateMode: val }),
  currentQuotationId: null,
  setCurrentQuotationId: (id) => set({ currentQuotationId: id }),
  binboxData: null,
  setBinboxData: (data) => set({ binboxData: data }),
  trialLimitReason: null,
  setTrialLimitReason: (reason) => set({ trialLimitReason: reason }),
}));